import { useEffect, useState } from 'react';

import { Button, message } from 'antd';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import AsyncBulkUpload from './AsyncBulkUpload';
import {
  JOB_NAMES,
  JOB_TYPES,
  POLLING_FREQ,
  TYPE_TITLE_MAP,
  UI_JOB_STATUS,
  JOB_STATUS,
  JOB_STATUS_MAPPING,
  getCTAText,
  ifButtonDisabled,
  getJobType
} from './BulkUploaderUtils';
import ROInfoMessage from './ROInfoMessage';
import apiConfig from '../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { getError, validator, delay } from '../../clientUtils';
import { FILE_TYPE_ATTRIBUTE } from '../../constants';

/**
    BULK_RO_UPDATE_SEND_FOR_APPROVAL,
    BULK_RO_UPDATE_APPROVE,
    BULK_RO_UPDATE_REJECT,
    BULK_RO_UPDATE_OPEN,
    BULK_RO_UPDATE_DISPATCH
 */

const onError = (error) => message.error(getError(error));

const onUpload = (jobName, file, onSuccess) => {
  CallApi(
    apiConfig.job.getPresignedURL,
    { jobName },
    null,
    undefined,
    async (res) => {
      try {
        const response = await axios.put(res, file, {
          headers: {
            'Content-Type': file.type
          }
        });

        if (response.status === 200) {
          onSuccess(file, res);
        }
      } catch {
        message.error('Failed to upload file');
      }
    },
    onError
  );
};
/**
 *
 * @param {string} type type of upload
 * @param {string} jobType [validation | submit]
 * @param {url} fileLink s3 link
 * @returns {object}
 */
const getBody = (type, jobType, fileLink, validationJobId) => {
  const fileType = `BULK_RO_UPDATE_${JOB_NAMES[type]}_${jobType === JOB_TYPES.validation ? 'INPUT_FILE' : 'SUBMIT_FILE'}`;
  const baseJobName = 'POSI_RETURN_ORDER_UPDATE_';

  if (jobType === JOB_TYPES.validation) {
    return {
      job_name: `${baseJobName}VALIDATE`,
      files: [
        {
          type: fileType,
          file_link: fileLink
        }
      ],
      metadata: { order_type: 'DOMESTIC_ONLINE_RETURN' }
    };
  }
  return {
    job_name: `${baseJobName}SUBMIT`,
    validate_job_id: validationJobId
  };
};

const AsyncROUploader = () => {
  const { type, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [sampleDownloadLink, setSampleDownloadLink] = useState(null);
  const [files, setFiles] = useState([]);
  const [jobStatus, setJobStatus] = useState(UI_JOB_STATUS.uploading);
  const [buttonLoading, setButtonLoading] = useState(false);

  const navigate = useNavigate();

  // fetching sample file
  useEffect(() => {
    setFiles([]);
    setJobStatus(UI_JOB_STATUS.uploading);
    CallApi(
      apiConfig.job.sampleFile,
      { upload_file_type: `BULK_RO_UPDATE_${JOB_NAMES[type]}` },
      null,
      setLoading,
      (res) => setSampleDownloadLink(res),
      () => message.error('Unable to download sample file!')
    );
  }, [type]);

  // if id is there then fetching the data
  useEffect(() => {
    let pollingId;
    if (id) {
      const getJobStatus = () => {
        CallApi(
          { ...apiConfig.job.status, path: apiConfig.job.status.path(id) },
          null,
          null,
          null,
          (res) => {
            // jobtype == submit and status == success
            if (
              (res.job_name.includes('SUBMIT') && res.status === JOB_STATUS.success) ||
              res.status === JOB_STATUS.submitted
            ) {
              message.info('File submitted, redirecting to listing page');
              delay(3000, () => navigate('/sales/ro'), onError);
            }
            if (JOB_STATUS_MAPPING[res.status] === 'end' && pollingId) {
              clearInterval(pollingId);
            }

            const file = res?.files?.find(
              (f) =>
                (f.type.includes(JOB_NAMES[type]) && f.type.includes('VALID')) ||
                (f.type.includes(JOB_NAMES[type]) && f.type.includes('SUBMIT'))
            );
            setFiles([{ ...file, summary: file?.validationSummary, link: file?.fileLink }]);
            setJobStatus(getJobType(res.status, res.job_name, [file?.validationSummary]));
          },
          onError
        );
      };

      getJobStatus();

      pollingId = setInterval(getJobStatus, POLLING_FREQ);
    }
    return () => clearInterval(pollingId);
  }, [navigate, type, id]);

  const uploadProps = {
    title: 'SKU File',
    description: 'Please upload the list of SKUs (in CSV format with max file size of 5 MB)',
    sampleDownloadText: 'Download Sample file',
    sampleDownloadLink,
    accept: FILE_TYPE_ATTRIBUTE.csv,
    files,
    validator,
    onUpload: (file) =>
      onUpload(`POSI_RETURN_ORDER_UPDATE_VALIDATE`, file, (f, link) => {
        setFiles([{ ...f, fileLink: link }]);
        setJobStatus(UI_JOB_STATUS.uploaded);
      }),
    onRemove: async () => {
      setFiles([]);
      setJobStatus(UI_JOB_STATUS.uploading);
      message.success('File deleted successfully!');
    },
    deleteDisabled: [UI_JOB_STATUS.submitted, UI_JOB_STATUS.submitting, UI_JOB_STATUS.validating].includes(jobStatus)
  };

  const fileUploaderProps = {
    message: '',
    items: [uploadProps]
  };

  const handleClick = () => {
    // jobStatus === uploaded -> validation job
    if (jobStatus === UI_JOB_STATUS.uploaded) {
      CallApi(
        apiConfig.job.validate,
        null,
        getBody(type, JOB_TYPES.validation, files?.[0]?.fileLink),
        setButtonLoading,
        (res) => {
          navigate(`/bulkuploader/ro/${type}/${res.job_id}`);
        },
        onError
      );
      return;
    }
    // jobStatus === validated -> submission job
    CallApi(
      apiConfig.job.submit,
      null,
      getBody(type, JOB_TYPES.submit, files?.[0]?.fileLink, id),
      setButtonLoading,
      (res) => {
        message.success('Job submitted');
        delay(2000);
        navigate(`/bulkuploader/ro/${type}/${res.job_id}`);
      },
      onError
    );
  };

  return (
    <AsyncBulkUpload
      button={
        <Button
          disabled={ifButtonDisabled(jobStatus, files?.validationSummary)}
          loading={buttonLoading}
          onClick={handleClick}
          type="primary">
          {getCTAText(jobStatus)}
        </Button>
      }
      info={<ROInfoMessage status={jobStatus} />}
      loading={loading}
      menuKey={`bu_ro_${type}`}
      title={TYPE_TITLE_MAP[type]}
      uploadersProp={[fileUploaderProps]}
    />
  );
};

export default AsyncROUploader;
